import React, { useMemo } from 'react';

import { Clinic } from '@/data/Clinics';
import { Dentist } from '@/data/Dentists';
import { Patient } from '@/data/Patients';
import { Request } from '@/data/Requests';
import { REQUEST_STATUS_OPTIONS } from '@/utils/constants';
import { capitalize } from '@/utils/helpers/capitalize';
import { formatDentistName } from '@/utils/helpers/formatDentistName';
import { formatName } from '@/utils/helpers/formatName';
import { isClinicName } from '@/utils/helpers/isClinicName';
import moment from 'moment';

import './RequestSummary.css';

interface RequestSummaryProps {
  clinic: Clinic | null;
  dentist: Dentist | null;
  patient?: Patient | null;
  request: Request | null;
  showPurpose?: boolean;
  showStatus?: boolean;
  durationInMinutes?: number;
}

const RequestSummary: React.FC<RequestSummaryProps> = ({
  clinic,
  dentist,
  patient,
  request,
  showPurpose = true,
  showStatus = true,
  durationInMinutes
}) => {
  const labels = {
    clinicName: 'Clinic',
    dentistName: 'Dentist',
    patientName: 'Patient',
    date: 'Date',
    time: 'Time',
    purpose: 'Purpose',
    status: 'Status',
    cancelReason: 'Reason for Cancellation',
    declineReason: 'Reason for Decline',
    rescheduleReason: 'Reason for Reschedule'
  };

  const isUsingClinicName = useMemo(() => {
    return isClinicName(`${dentist?.first_name} ${dentist?.last_name}`);
  }, [dentist?.first_name, dentist?.last_name]);

  return (
    <div id="requestSummary">
      <div className="requestDetail">
        <p className="label">{labels.clinicName}</p>
        <p className="value">
          {!isUsingClinicName
            ? clinic?.name
            : formatDentistName({
                dentist,
                returnDentistFullName: true
              })}
        </p>
      </div>
      {!isUsingClinicName && (
        <div className="requestDetail">
          <p className="label">{labels.dentistName}</p>
          <p className="value">
            {dentist
              ? formatDentistName({
                  dentist,
                  returnDentistFullName: true
                })
              : '--'}
          </p>
        </div>
      )}
      {patient && (
        <div className="requestDetail">
          <p className="label">{labels.patientName}</p>
          <p className="value sensitive">
            {patient.first_name && patient.last_name
              ? `${formatName(patient.first_name)} ${formatName(patient.last_name)}`
              : '--'}
          </p>
        </div>
      )}
      <div className="requestDetail">
        <p className="label">{labels.date}</p>
        <p className="value">
          {request?.date ? moment(request.date).format('MMMM D, YYYY') : '--'}
        </p>
      </div>
      <div className="requestDetail">
        <p className="label">{labels.time}</p>
        <p className="value">
          {request?.start_time && request?.end_time
            ? `${moment(request.start_time, 'HH:mm').format('h:mm A')} - ${durationInMinutes ? moment(request.start_time, 'HH:mm').add(durationInMinutes, 'minutes').format('h:mm A') : moment(request.end_time, 'HH:mm').format('h:mm A')}`
            : '--'}
        </p>
      </div>
      {showPurpose && (
        <div className="requestDetail">
          <p className="label">{labels.purpose}</p>
          <p className="value">{request?.purpose || '--'}</p>
        </div>
      )}
      {showStatus && (
        <div className="requestDetail">
          <p className="label">{labels.status}</p>
          <p className="value">
            {capitalize(request?.status || REQUEST_STATUS_OPTIONS.PENDING)}
          </p>
        </div>
      )}
      {request?.cancel_reason && (
        <div className="requestDetail">
          <p className="label">{labels.cancelReason}</p>
          <p className="value">{request?.cancel_reason}</p>
        </div>
      )}
      {request?.decline_reason && (
        <div className="requestDetail">
          <p className="label">{labels.declineReason}</p>
          <p className="value">{request?.decline_reason}</p>
        </div>
      )}
      {request?.reschedule_reason && (
        <div className="requestDetail">
          <p className="label">{labels.rescheduleReason}</p>
          <p className="value">{request?.reschedule_reason}</p>
        </div>
      )}
    </div>
  );
};

export default RequestSummary;
